// src/pages/payments/PaymentCallback.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { MessageContainer, Spinner, Button } from '../registeredUsers/styles/BuyStall.styles';
import { pesapalService } from './pesapal.service';

interface PaymentDetails {
  orderTrackingId: string;
  merchantReference: string;
  paymentStatus: string;
  amount: string;
  currency: string;
  paymentMethod?: string;
  confirmationCode?: string;
  paymentAccount?: string;
}

const PaymentCallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'failed'>('loading');
  const [message, setMessage] = useState('Processing your payment...');
  const [stallNumber, setStallNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      const queryParams = new URLSearchParams(location.search);
      
      // Log all URL parameters for debugging
      console.log('=== Full Callback URL ===');
      console.log('URL:', window.location.href);
      console.log('Search params:', location.search);
      console.log('All parameters:');
      queryParams.forEach((value, key) => {
        console.log(`  ${key}: ${value}`);
      });
      
      // Try different possible parameter names from Pesapal
      const orderTrackingId = 
        queryParams.get('OrderTrackingId') || 
        queryParams.get('orderTrackingId') ||
        queryParams.get('order_tracking_id') ||
        queryParams.get('tracking_id');
      
      const merchantReference = 
        queryParams.get('OrderMerchantReference') || 
        queryParams.get('orderMerchantReference') ||
        queryParams.get('merchant_reference') ||
        queryParams.get('merchantReference');
      
      // Parameter from our callback URL
      const stallNumberFromUrl = queryParams.get('stallNumber');

      console.log('=== Extracted Parameters ===');
      console.log('Order Tracking ID:', orderTrackingId);
      console.log('Merchant Reference:', merchantReference);
      console.log('Stall Number from URL:', stallNumberFromUrl);

      // If no order tracking ID, show all params to help debug
      if (!orderTrackingId) {
        const allParams = Array.from(queryParams.entries())
          .map(([key, value]) => `${key}=${value}`)
          .join(', ');
        
        setStatus('failed');
        setMessage(`Invalid payment callback. Missing transaction ID from Pesapal. Received parameters: ${allParams || 'none'}`);
        console.error('Missing OrderTrackingId. All params:', allParams);
        return;
      }

      if (!stallNumberFromUrl) {
        setStatus('failed');
        setMessage('Invalid payment callback. Missing stall number from callback URL. Please contact support with your order tracking ID: ' + orderTrackingId);
        
        // Still try to show what we have
        setPaymentDetails({
          orderTrackingId,
          merchantReference: merchantReference || 'N/A',
          paymentStatus: 'Unknown',
          amount: '0',
          currency: 'UGX',
        });
        return;
      }

      // Set stall number early
      setStallNumber(stallNumberFromUrl);

      try {
        setMessage('Verifying payment with Pesapal...');
        
        // Verify payment status with Pesapal
        const paymentData = await pesapalService.getOrderStatus(orderTrackingId);
        
        console.log('Payment verification result:', paymentData);

        const paymentStatus = paymentData.payment_status;
        
        // Use stall number from URL as the primary source, but keep all transaction IDs
        const stallRef = stallNumberFromUrl;

        console.log('=== Payment Callback Debug ===');
        console.log('Order Tracking ID (Pesapal UUID):', orderTrackingId);
        console.log('Merchant Reference from Pesapal URL:', merchantReference);
        console.log('Merchant Reference from API:', paymentData.merchant_reference);
        console.log('Stall Number from our callback URL:', stallNumberFromUrl);
        console.log('Using stall reference:', stallRef);

        // Verify all transaction IDs match or store them for reference
        if (paymentData.merchant_reference && paymentData.merchant_reference !== stallRef) {
          console.warn('Merchant reference mismatch:', {
            expected: stallRef,
            received: paymentData.merchant_reference
          });
        }

        // Store payment details for display
        setPaymentDetails({
          orderTrackingId,
          merchantReference: stallRef,
          paymentStatus: paymentData.payment_status,
          amount: paymentData.amount,
          currency: paymentData.currency,
          paymentMethod: paymentData.payment_method,
          confirmationCode: paymentData.confirmation_code,
          paymentAccount: paymentData.payment_account,
        });

        setMessage('Updating stall information...');

        // First, try to find the stall in pendingStalls
        const pendingStallDocRef = doc(db, 'pendingStalls', stallRef);
        const pendingStallDoc = await getDoc(pendingStallDocRef);

        console.log('Pending stall exists:', pendingStallDoc.exists());

        if (!pendingStallDoc.exists()) {
          // Check if already in 'stalls' collection
          const stallDocRef = doc(db, 'stalls', stallRef);
          const stallDoc = await getDoc(stallDocRef);
          
          console.log('Stall exists in stalls collection:', stallDoc.exists());
          
          if (!stallDoc.exists()) {
            // Log detailed error information
            console.error('Stall not found. Details:', {
              stallRef,
              merchantReference,
              orderTrackingId,
              paymentData
            });
            throw new Error(`Stall record not found in database. Searched for: ${stallRef}. Please ensure the stall was created before payment.`);
          }

          const stallData = stallDoc.data();
          setStallNumber(stallData.stallNumber);
          setBusinessName(stallData.businessName);

          // Update existing stall
          await updateDoc(stallDocRef, {
            paymentStatus: paymentStatus === 'Completed' ? 'SUCCESS' : 'FAILED',
            paymentDate: new Date(),
            isActive: paymentStatus === 'Completed',
            transactionId: orderTrackingId, // Pesapal's order tracking ID
            merchantReference: paymentData.merchant_reference, // Pesapal's merchant reference
            amountPaid: paymentData.amount,
            lastPaymentMethod: paymentData.payment_method,
            confirmationCode: paymentData.confirmation_code,
          });

          if (paymentStatus === 'Completed') {
            setStatus('success');
            setMessage('Payment successful! Your stall has been updated.');
          } else {
            setStatus('failed');
            setMessage(`Payment ${paymentStatus}. Please try again or contact support.`);
          }
          return;
        }

        // Process pending stall
        const stallData = pendingStallDoc.data();
        setStallNumber(stallData.stallNumber);
        setBusinessName(stallData.businessName);

        if (paymentStatus === 'Completed') {
          // Calculate subscription expiry date
          const expiryDate = new Date();
          const duration = stallData.subscriptionDuration;
          
          if (duration === 'Weekly') {
            expiryDate.setDate(expiryDate.getDate() + 7);
          } else if (duration === 'Monthly') {
            expiryDate.setMonth(expiryDate.getMonth() + 1);
          } else if (duration === '6-Monthly') {
            expiryDate.setMonth(expiryDate.getMonth() + 6);
          } else if (duration === '12-Monthly') {
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          }

          // Move stall to 'stalls' collection with payment info
          const stallDocRef = doc(db, 'stalls', stallRef);
          await setDoc(stallDocRef, {
            ...stallData,
            paymentStatus: 'SUCCESS',
            paymentDate: new Date(),
            isActive: true,
            subscriptionExpiryDate: expiryDate,
            transactionId: orderTrackingId, // Pesapal's order tracking ID
            merchantReference: paymentData.merchant_reference, // Pesapal's merchant reference
            stallNumber: stallRef, // Our stall number
            amountPaid: paymentData.amount,
            paymentMethod: paymentData.payment_method,
            confirmationCode: paymentData.confirmation_code,
            paymentAccount: paymentData.payment_account,
          });

          // Delete from pendingStalls
          await deleteDoc(pendingStallDocRef);

          setStatus('success');
          setMessage('Payment successful! Your stall is now active.');
        } else {
          // Update pendingStalls with failed status
          await updateDoc(pendingStallDocRef, {
            paymentStatus: 'FAILED',
            transactionId: orderTrackingId, // Pesapal's order tracking ID
            merchantReference: paymentData.merchant_reference, // Pesapal's merchant reference
            lastPaymentAttempt: new Date(),
          });
          
          setStatus('failed');
          setMessage(`Payment ${paymentStatus}. Your stall registration is saved. Please try payment again.`);
        }
      } catch (error) {
        console.error('Payment verification failed:', error);
        setStatus('failed');
        setMessage(`Failed to process payment: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support with your transaction details.`);
      }
    };

    processCallback();
  }, [location]);

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '900px',
      margin: '0 auto',
      textAlign: 'center'
    }}>
      <h2>Payment Processing</h2>

      {status === 'loading' && <Spinner />}

      <MessageContainer
        type={
          status === 'success' ? 'success' :
          status === 'failed' ? 'error' :
          'info'
        }
      >
        {message}
      </MessageContainer>

      {/* Debug info - show URL parameters */}
      {status === 'failed' && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          textAlign: 'left',
          fontSize: '0.9rem',
          border: '1px solid #ffc107'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Debug Information:</p>
          <p style={{ margin: '0.25rem 0', wordBreak: 'break-all' }}>
            <strong>Full URL:</strong><br />
            <code style={{ fontSize: '0.8rem' }}>{window.location.href}</code>
          </p>
        </div>
      )}

      {/* Payment Summary */}
      {paymentDetails && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'left',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#343a40' }}>Payment Summary</h3>
          
          {/* Business Information */}
          {stallNumber && (
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
              <p style={{ margin: '0.5rem 0' }}>
                <strong>Stall Number:</strong> {stallNumber}
              </p>
              {businessName && (
                <p style={{ margin: '0.5rem 0' }}>
                  <strong>Business Name:</strong> {businessName}
                </p>
              )}
            </div>
          )}
          
          {/* Transaction IDs */}
          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
            <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              <strong>Order Tracking ID:</strong><br />
              <code style={{ fontSize: '0.85rem', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>
                {paymentDetails.orderTrackingId}
              </code>
            </p>
            
            {paymentDetails.merchantReference && (
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
                <strong>Merchant Reference:</strong><br />
                <code style={{ fontSize: '0.85rem', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>
                  {paymentDetails.merchantReference}
                </code>
              </p>
            )}
          </div>
          
          {/* Payment Details */}
          <div>
          
          <p style={{ margin: '0.5rem 0' }}>
            <strong>Amount:</strong> {paymentDetails.currency} {parseFloat(paymentDetails.amount).toLocaleString()}
          </p>
          
          <p style={{ margin: '0.5rem 0' }}>
            <strong>Status:</strong> <span style={{
              color: status === 'success' ? '#28a745' : status === 'failed' ? '#dc3545' : '#6c757d',
              fontWeight: 'bold'
            }}>
              {paymentDetails.paymentStatus}
            </span>
          </p>
          
          {paymentDetails.paymentMethod && (
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Payment Method:</strong> {paymentDetails.paymentMethod}
            </p>
          )}
          
          {paymentDetails.confirmationCode && (
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Confirmation Code:</strong> {paymentDetails.confirmationCode}
            </p>
          )}
          
            {paymentDetails.paymentAccount && (
              <p style={{ margin: '0.5rem 0' }}>
                <strong>Payment Account:</strong> {paymentDetails.paymentAccount}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {status === 'success' && (
        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <Button onClick={() => navigate(`/user-listings?stallNumber=${stallNumber}`)}>
            Add Items Here
          </Button>
          <Button
            onClick={() => navigate('/')}
            style={{ backgroundColor: '#6c757d' }}
          >
            Go to Home
          </Button>
        </div>
      )}

      {status === 'failed' && (
        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button onClick={() => navigate('/buy-stall')}>
            Try Payment Again
          </Button>
          <Button
            onClick={() => navigate('/contact-support')}
            style={{ backgroundColor: '#f0f0f0', color: '#333' }}
          >
            Contact Support
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentCallback;